import React, { useEffect, useState } from "react";
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Stepper,
  Step,
  StepLabel,
  Grid,
  TextField,
} from '@material-ui/core';
import { observer } from "mobx-react";
import { useStore } from '@roc/client-portal-shared/hooks';
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { makeStyles } from '@material-ui/core/styles';
import { Button, createNumberFormatField, createTextField } from '@roc/ui';
import { Email, Phone, Sms, MoreHoriz } from "@material-ui/icons";
import { PhoneCallCadenceStep } from "./phoneCallCadenceStep";
import { MIXED_OBJECT_TYPE } from 'libs/feature-backoffice-users/src/backOfficeUsers/components/leads/constants/constants';
import { SendEmailsChannel } from "./cadenceChannels/sendEmailsChannel";
import { SendMessagesCadenceModal } from "./cadenceChannels/sendTextsChannel";
import { CadenceDetails } from "./cadenceDetails";
import { CadenceEmailTemplates } from "./cadenceEmailsTemplates";

const useStyles = makeStyles((theme) => ({
  channelButtonContainer: {
    marginTop: '30px',
    padding: '10px',
  },
  dayNumberInfoContainer: {
    marginTop: '10px',
    padding: '2px',
  },
  dialogActions: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    marginTop: '20px',
    padding: '20px',
  },
}));

export const AddCadenceStepModal = observer(({ openModal, handleCloseModal, stepToEdit = null }) => {
  const [activeStep, setActiveStep] = useState(0);
  const { salesforceLeadStore, cadenceStore, ringCentralStore, globalStore } = useStore();
  const { cadenceForm } = cadenceStore;
  const [alignment, setAlignment] = useState(stepToEdit?.type || null);
  const [title, setTitle] = useState(stepToEdit?.title || null);
  const [day, setDay] = useState(stepToEdit?.day || null);
  const classes = useStyles();

  useEffect(() => {
    if (!openModal) {
      return;
    }
    if (stepToEdit) {
      setupCadenceEdition();
    } else {
      cadenceForm.reset();
      setTitle(null);
      setDay(null);
      setAlignment(null);
      setActiveStep(0);
      ringCentralStore.resetContent();
      ringCentralStore.imageInsertionOption = 'noImage';
      ringCentralStore.mockTemplateInsertions = '';
      salesforceLeadStore.sendEmailsStore.selectedTemplateOption = '';
      salesforceLeadStore.sendEmailsStore.selectedTemplateId = null;
    }
  }, [stepToEdit, openModal]);

  const handleCreateTemplate = async () => {
    if (!ringCentralStore.label || !ringCentralStore.templateBusinessUnit || !ringCentralStore.templateValue.replace('<p>', '').replace('</p>', '') || !ringCentralStore.templateType || (ringCentralStore.templateType == 'email' && !ringCentralStore.templateSubject)) {
      globalStore.notificationStore.showErrorNotification({
        message: 'All fields are required',
      });
    } else if (ringCentralStore.templateType != 'text' && ((ringCentralStore.templateInsertions.length != 1 && ringCentralStore.imageInsertionOption == 'oneImage') ||
      (ringCentralStore.templateInsertions.length != 2 && ringCentralStore.imageInsertionOption == 'twoImagesSideBySide'))) {
      globalStore.notificationStore.showErrorNotification({
        message: 'Add the required images to the template',
      });
    } else {
      ringCentralStore.setFromJourneyBuilder(true);
      await ringCentralStore.insertTemplate(cadenceStore.templateAtt, (templateId) => {
        cadenceStore.cadenceForm.onFieldChange('emailTemplateId', templateId);
        cadenceStore.createStep()
      });
    }
  }

  const handleUpdateTemplate = () => {
    if (!ringCentralStore.label || !ringCentralStore.templateValue.replace('<p>', '').replace('</p>', '') || !ringCentralStore.templateType || (ringCentralStore.templateType == 'email' && !ringCentralStore.templateSubject)) {
      globalStore.notificationStore.showErrorNotification({
        message: 'All fields are required',
      });
      return false;
    } else if (ringCentralStore.templateType != 'text' && ((ringCentralStore.templateInsertions.length != 1 && ringCentralStore.imageInsertionOption == 'oneImage') ||
      (ringCentralStore.templateInsertions.length != 2 && ringCentralStore.imageInsertionOption == 'twoImagesSideBySide'))) {
      globalStore.notificationStore.showErrorNotification({
        message: 'Add the required images to the template',
      });
      return false;
    }
    ringCentralStore.setFromJourneyBuilder(true);
    ringCentralStore.updateTemplate(cadenceStore.templateAtt);
    return true;
  }

  const setupCadenceEdition = () => {
    cadenceForm.onFieldChange('title', stepToEdit?.title)
    cadenceForm.onFieldChange('day', stepToEdit?.day)
    cadenceForm.onFieldChange('stepNumber', stepToEdit?.stepNumber)
    setAlignment(stepToEdit?.type)
    cadenceForm.onFieldChange('stepNumber', stepToEdit?.stepNumber)
    cadenceForm.onFieldChange('type', stepToEdit?.type);
  }

  const handleChange = (event, newAlignment) => setAlignment(newAlignment);
  const handleNext = () => {
    cadenceForm.runFormValidation()
    if (cadenceForm.form.meta.isValid) {
      setTitle(cadenceForm.form.fields.title.value);
      setDay(cadenceForm.form.fields.day.value);
      setActiveStep((prev) => prev + 1);
    }
  }
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleSave = () => {
    if (alignment === 'other') {
      cadenceForm.runFormValidation()
      if (!cadenceForm.form.meta.isValid) return;
      cadenceForm.onFieldChange('type', alignment);
      setTitle(cadenceForm.form.fields.title.value);
      setDay(cadenceForm.form.fields.day.value);
    } else {
      cadenceForm.onFieldChange('type', alignment);
      cadenceForm.onFieldChange('day', day);
      cadenceForm.onFieldChange('title', title);
      cadenceForm.onFieldChange('stepNumber', stepToEdit?.stepNumber)
    }
    if (stepToEdit) {
      if (alignment === 'email' && cadenceStore.creatingTemplate && !handleUpdateTemplate()) {
        return;
      }
      cadenceStore.editStep()
    } else {
      if (alignment === 'email' && cadenceStore.creatingTemplate) {
        handleCreateTemplate();
      } else {
        cadenceStore.createStep();
      }
    }
    setAlignment(null);
    setActiveStep(0)
    handleCloseModal()
    salesforceLeadStore.sendEmailsStore.reset()
  }

  return (
    <Dialog
      open={openModal}
      onClose={() => {
        handleCloseModal();
        salesforceLeadStore.sendEmailsStore.reset()
      }}
      PaperProps={{
        style: {
          minWidth: '800px',
          maxWidth: '90vw',
          minHeight: '600px',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>{stepToEdit ? "Edit Step" : "Add Step"}</DialogTitle>
      <DialogContent style={{ width: '100%', height: '100%' }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          <Step><StepLabel>Channel</StepLabel></Step>
          <Step><StepLabel>Content</StepLabel></Step>
        </Stepper>

        {activeStep === 0 && (
          <FormControl fullWidth>
            <div className={classes.channelButtonContainer}>
              <Typography variant="h6">Channel</Typography>
              <ToggleButtonGroup
                color="primary"
                value={alignment}
                exclusive
                onChange={handleChange}
                aria-label="Platform"
              >
                <ToggleButton value="email">
                  <Email style={{ marginRight: 8 }} /> Email
                </ToggleButton>
                <ToggleButton value="phone">
                  <Phone style={{ marginRight: 8 }} /> OutBound Call
                </ToggleButton>
                <ToggleButton value="text">
                  <Sms style={{ marginRight: 8 }} /> Text
                </ToggleButton>
                <ToggleButton value="other">
                  <MoreHoriz style={{ marginRight: 8 }} /> Other
                </ToggleButton>
              </ToggleButtonGroup>
            </div>

            <div className={classes.dayNumberInfoContainer}>
              <Typography variant="h6">Step Title</Typography>
              <Grid container alignItems="center" spacing={1} style={{ marginTop: '10px' }}>
                <Grid item xs={6}>
                  {createTextField({
                    label: '',
                    fieldName: 'title',
                    testId: 'title',
                    store: cadenceForm,
                  })}
                </Grid>
              </Grid>
            </div>

            <div className={classes.dayNumberInfoContainer}>
              <Typography variant="h6">Day number</Typography>
              <Typography variant="body2">
                The day this step becomes due. Day 1 steps occur the same day a person is added to the cadence.
              </Typography>

              <Grid container alignItems="center" spacing={1} style={{ marginTop: '10px' }}>
                <Grid item xs={3}>
                  {createNumberFormatField({
                    label: 'Day',
                    fieldName: 'day',
                    testId: 'day',
                    store: cadenceForm,
                  })}
                </Grid>
              </Grid>
            </div>
          </FormControl>
        )}

        {activeStep === 1 && (() => {
          switch (alignment) {
            case 'email':
              return (
                <>
                  {(stepToEdit && (stepToEdit.stepContent?.emailTemplateId === null || stepToEdit.stepContent?.emailTemplateId === ""))
                    ?
                    <SendEmailsChannel
                      handleClose={() => window.history.back()}
                      salesforceLeadStore={salesforceLeadStore}
                      type={MIXED_OBJECT_TYPE}
                      stepToEdit={stepToEdit}
                    />
                    : <CadenceEmailTemplates
                      handleClose={() => window.history.back()}
                      disableEditTemplateBody
                      salesforceLeadStore={salesforceLeadStore}
                      type={MIXED_OBJECT_TYPE}
                      stepToEdit={stepToEdit}
                    />}
                </>
              );
            case 'phone':
              return <PhoneCallCadenceStep stepToEdit={stepToEdit} />;
            case 'text':
              return (
                <SendMessagesCadenceModal
                  handleClose={() => window.history.back()}
                  salesforceLeadStore={salesforceLeadStore}
                  type={MIXED_OBJECT_TYPE}
                  stepToEdit={stepToEdit}
                />
              );
            default:
              return (
                <Typography variant="h5" align="center">
                  Please select a valid channel
                </Typography>
              );
          }
        })()}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          testId="backB"
          disabled={activeStep === 0}
          onClick={handleBack}
          variant="contained"
          color="primary"
        >
          Back
        </Button>
        {(activeStep === 1 || alignment === 'other') ? (
          <Button
            testId="finishB"
            onClick={handleSave}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        ) : (
          <Button
            testId="nextB"
            disabled={!alignment}
            onClick={handleNext}
            variant="contained"
            color="primary"
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});
